import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { BoundedApiError } from '@bounded/api-client';
import { routeTree } from './routeTree.gen';
import { Pending } from '@bounded/ui/pending';
import { I18nProvider, detectLocale, loadMessages } from './i18n';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchIntervalInBackground: false,
      // Client errors are answers, not blips: don't hammer on 4xx.
      retry: (count, error) => !(error instanceof BoundedApiError && error.status < 500) && count < 2,
    },
    // Writes are never retried automatically (idempotency keys are the caller's job).
    mutations: { retry: false },
  },
});

const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', defaultPreloadStaleTime: 0, scrollRestoration: true, defaultPendingComponent: Pending, defaultPendingMs: 200 });
declare module '@tanstack/react-router' { interface Register { router: typeof router } }

// Load the chosen language before the first render so the page never flashes English first.
const locale = detectLocale();
const messages = await loadMessages(locale);
document.documentElement.lang = locale;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nProvider initial={locale} messages={messages}>
      <QueryClientProvider client={queryClient}><RouterProvider router={router} /></QueryClientProvider>
    </I18nProvider>
  </StrictMode>,
);
